import React, { useState, useContext, useRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FavouritesContext } from "../contexts/FavouritesContext";

// YouTube serves mqdefault.jpg (320x180) for every public video, no API key needed.
const getYouTubeThumbUrl = (videoId) =>
  videoId ? `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg` : null;

const PlaylistTile = (props) => {
  const { isFavourite, toggleFavourite } = useContext(FavouritesContext);
  const textRef = useRef(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props.video.video_id,
    disabled: !props.isEditing,
  });

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const style =
    props.currentVideoIndex === props.index
      ? "playlist-item active"
      : "playlist-item";

  const isActiveSong = props.currentVideoIndex === props.index;
  const isFav = isFavourite(props.video.video_id);

  const [isHover, setIsHover] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Store the id that failed rather than a boolean, so the flag self-resets
  // if this component instance is reused for a different video.
  const [failedThumbId, setFailedThumbId] = useState(null);

  const videoId = props.video?.video_id;
  const thumbUrl = getYouTubeThumbUrl(videoId);
  const showThumb = Boolean(thumbUrl) && failedThumbId !== videoId;

  const handleMouseEnter = () => {
    setIsHover(true);
    if (textRef.current) {
      const scroll = textRef.current.scrollWidth;
      const client = textRef.current.clientWidth;
      if (scroll > client) {
        setIsOverflowing(true);
        textRef.current.style.setProperty("--scroll-amount", `-${scroll - client}px`);
      } else {
        setIsOverflowing(false);
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHover(false);
    setIsOverflowing(false); // Reset overflow state
  };

  const onTileClickHandler = () => {
    props.onClick(props.video);
  };

  const onFavouriteClick = (e) => {
    e.stopPropagation();
    toggleFavourite(props.video);
  };

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle}
      className={style}
      onClick={onTileClickHandler}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="playlist-tile-wrapper">
        {props.isEditing && (
          <i
            className="fa-solid fa-grip-vertical drag-handle"
            {...attributes}
            {...listeners}
          ></i>
        )}
        <div className="playlist-thumb">
          {showThumb ? (
            <img
              className="playlist-thumb-img"
              src={thumbUrl}
              alt=""
              width="40"
              height="40"
              loading="lazy"
              decoding="async"
              draggable="false"
              onError={() => setFailedThumbId(videoId)}
              onLoad={(e) => {
                // YouTube answers 200 with its 120x90 grey placeholder for some
                // deleted/private videos; a real mqdefault is always 320 wide.
                if (e.currentTarget.naturalWidth <= 120) setFailedThumbId(videoId);
              }}
            />
          ) : (
            <i className="fa-solid fa-music playlist-thumb-fallback"></i>
          )}
          {(isHover || isActiveSong) && (
            <span className="playlist-thumb-overlay">
              <i className="fa-solid fa-play"></i>
            </span>
          )}
        </div>
        <div
          className={`playlist-tile-text ${isHover && isOverflowing ? "marquee-active" : ""}`}
          style={{ flexGrow: 1 }}
          ref={textRef}
        >
          <span>
            {props.showArtist
              ? props.video.artist + " - " + props.video.title
              : props.video.title}
          </span>
        </div>
        {(props.isEditing || !props.showArtist) && (
          <i
            className={`favourite-btn fa-heart ${isFav ? "fa-solid active" : "fa-regular"}`}
            onClick={onFavouriteClick}
            title={isFav ? "Odebrat z oblíbených" : "Přidat do oblíbených"}
            style={{ visibility: props.isEditing || isHover || isFav ? "visible" : "hidden" }}
          ></i>
        )}
      </div>
    </div>
  );
};

export default PlaylistTile;
